import { string, object, func, bool, node } from 'prop-types'
import { Fragment, useState } from 'react'
import { Asset, renderIcon, kuiperClassNames } from '@opallabs/kuiper'
import { AccountName } from '../../../../../AccountName'
import { ContentAvatar } from '../../../../../ContentAvatar'
import { ContentTextInput } from '../../../../../ContentTextInput'
import { sharedColors } from '../sharedStyles'
import { styles } from './styles.js'

export const DesktopSingle = ({
  accountImage,
  assetSlot,
  collabEnabled,
  collabAccount,
  content,
  isPresenting,
  isSnapshot = false,
  onContentChange,
  onSoundChange,
  onExpandAsset,
  onRemoveAsset,
  readOnly,
  sound,
  truncate,
  username,
  video,
}) => {
  const [isHovered, setHovered] = useState(false)
  return (
    <Fragment>
      <div
        css={styles.assetContainer}
        className={kuiperClassNames('tiktok-asset-container')}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <span css={styles.assetContainer.closeIcon}>
          {renderIcon('tiktok-desktop-close', {
            ...styles.assetIconStyles,
            ariaMode: 'hidden',
          })}
        </span>

        {assetSlot && !video ? (
          assetSlot
        ) : (
          <Asset
            hasAssetOverlay={!isSnapshot && (Boolean(video) || isPresenting)}
            assetReference={video}
            disableCoverAsset={false}
            isHovered={isHovered}
            imageFit="contain"
            placeholder={true}
            preview={isSnapshot}
            readOnly={readOnly}
            removeable={!isPresenting}
            onEmbiggen={() => onExpandAsset(video.id)}
            onRemove={() => onRemoveAsset(video.id)}
          />
        )}

        {!assetSlot && (
          <Fragment>
            <span css={styles.assetContainer.chevronWest}>
              {renderIcon('tiktok-desktop-chevron-west', {
                ...styles.assetIconStyles,
                ariaMode: 'hidden',
              })}
            </span>
            <span css={styles.assetContainer.chevronEast}>
              {renderIcon('tiktok-desktop-chevron-east', {
                ...styles.assetIconStyles,
                ariaMode: 'hidden',
              })}
            </span>
          </Fragment>
        )}
      </div>

      <div
        css={styles.postContent}
        className={kuiperClassNames('tiktok-post-content')}
      >
        <div
          css={styles.postHeader}
          className={kuiperClassNames('tiktok-post-header')}
        >
          <div>
            <ContentAvatar
              assetReference={accountImage}
              size={28}
              style={styles.avatar}
            />
            <div css={styles.nameAndSubAccount}>
              <div
                css={styles.nameRow}
                className={kuiperClassNames({
                  [styles.withCollab]: collabEnabled,
                })}
              >
                <AccountName
                  color={sharedColors.textColor}
                  noAccountColor={sharedColors.orange}
                  name={username}
                  style={styles.accountName}
                  size={15}
                />
                {collabEnabled && (
                  <Fragment>
                    <span css={styles.collabSeparator}> and </span>
                    <AccountName
                      color={
                        collabAccount
                          ? sharedColors.textColor
                          : sharedColors.orange
                      }
                      name={collabAccount || 'No collab account'}
                      style={styles.accountName}
                      size={15}
                    />
                  </Fragment>
                )}
              </div>
              <p css={styles.subAccountName(username)}>
                <span>{username || 'No account selected'}</span>{' '}
                <span css={styles.bulletPoint}>&#x25CF;</span> 1m ago
              </p>
            </div>
          </div>
          <div>
            <span css={styles.followButton}>
              <p>Follow</p>
            </span>
          </div>
        </div>

        <div
          css={styles.postInputs}
          className={kuiperClassNames('tiktok-post-inputs')}
        >
          <ContentTextInput
            characterLimit={2200}
            characterCounterOptions={{ position: 'right', offset: 70 }}
            minRows={1}
            maxRows={4}
            maxLength={2200}
            truncate={truncate}
            truncationLimit={140}
            onChange={onContentChange}
            readOnly={readOnly}
            placeholder="Add caption..."
            placeholderStyle={styles.postInputs.placeholderInput}
            style={styles.postInputs.captionInput}
            value={content || ''}
          />
          <div css={styles.postInputs.soundWrapper}>
            {renderIcon('tiktok-desktop-sound', {
              ...styles.postInputs.soundIcon,
              ariaMode: 'hidden',
            })}
            <ContentTextInput
              minRows={1}
              maxRows={4}
              truncate={truncate}
              truncationLimit={25}
              onChange={onSoundChange}
              placeholder="Add sound..."
              readOnly={readOnly}
              style={styles.postInputs.soundInput}
              value={sound || ''}
            />
          </div>
        </div>

        <div
          css={styles.postShareBar}
          className={kuiperClassNames('tiktok-post-share-bar')}
        >
          <ul>
            <li>
              {renderIcon('tiktok-desktop-heart', { ariaMode: 'hidden' })}{' '}
              <p>###</p>
            </li>
            <li>
              {renderIcon('tiktok-desktop-comment', { ariaMode: 'hidden' })}{' '}
              <p>###</p>
            </li>
          </ul>
          <ul className="tiktok-social-icons">
            <li>
              {renderIcon('tiktok-desktop-social-facebook', {
                ...styles.socialIcon,
                ariaMode: 'hidden',
              })}
            </li>
            <li>
              {renderIcon('tiktok-desktop-social-pinterest', {
                ...styles.socialIcon,
                ariaMode: 'hidden',
              })}
            </li>
            <li>
              {renderIcon('tiktok-desktop-social-twitter', {
                ...styles.socialIcon,
                ariaMode: 'hidden',
              })}
            </li>
            <li>
              {renderIcon('tiktok-desktop-social-embed', {
                ...styles.socialIcon,
                ariaMode: 'hidden',
              })}
            </li>
            <li>
              {renderIcon('tiktok-desktop-social-url', {
                ...styles.socialIcon,
                ariaMode: 'hidden',
              })}
            </li>
          </ul>
        </div>
      </div>
    </Fragment>
  )
}

DesktopSingle.propTypes = {
  accountImage: object,
  assetSlot: node,
  collabEnabled: bool,
  collabAccount: string,
  content: string,
  isHovered: bool,
  isPresenting: bool,
  isSnapshot: bool,
  onContentChange: func,
  onExpandAsset: func,
  onRemoveAsset: func,
  onSoundChange: func,
  readOnly: bool,
  sound: string,
  truncate: bool,
  username: string,
  video: object,
}
